import { supabase } from '@/lib/supabaseClient';
import { createJob, formatUsd } from '@/lib/jobsData';
import * as pqi from '@/lib/pqiTracker';
import { normalizeStatus } from '@/lib/estimateLifecycle';

/**
 * Schema notes (Phase 15 — current):
 *  - The `estimates` Supabase table has columns: id, job_id, client_id, title,
 *    status, subtotal, tax, total, notes, sent_at, approved_at, created_at.
 *  - All rich estimate data is JSON-encoded into the `notes` text column under
 *    a sentinel header.
 *
 *  Section shape (per Phase 15):
 *    section = {
 *      id, name, notes,
 *      items:        [ { id, desc, qty, unit, rate, ho_pick:boolean } ],
 *      materials:    [ { id, desc, qty, unit, rate } ],
 *      materials_notes: string,
 *      allowances:   [ { id, desc, qty, unit, rate } ],   // informational only
 *      allowances_notes: string,
 *      bottom_line_mode:  boolean,                        // hide line costs in preview
 *      bottom_line_total: number | null,                  // manual section total override
 *    }
 *
 *  Estimate-level overview block (Phase 14, retained):
 *    overview = { description, scope_summary, address, est_start, est_end, special_conditions }
 *
 *  Totals math:
 *    section_items_total       = sum of items[]
 *    section_materials_total   = sum of materials[]
 *    section_allowances_total  = sum of allowances[]   (informational)
 *    section_computed_total    = items + materials     (the contract amount)
 *    section_display_total     = bottom_line_mode ? (bottom_line_total ?? computed) : computed
 *
 *    subtotal           = sum of all section_display_total across sections
 *    allowances_total   = sum of all allowances across sections (informational)
 *    tax                = subtotal * (tax_rate / 100)
 *    total              = subtotal + tax
 */
const NOTES_SENTINEL = '__KOCORE_ESTIMATE_V1__';

const encodeBlob = (blob) => `${NOTES_SENTINEL}${JSON.stringify(blob)}`;

const decodeBlob = (notes) => {
  if (!notes || typeof notes !== 'string') return null;
  if (!notes.startsWith(NOTES_SENTINEL)) return null;
  try {
    return JSON.parse(notes.slice(NOTES_SENTINEL.length));
  } catch (e) {
    return null;
  }
};

const genId = (prefix) => {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().split('-')[0].toUpperCase()
      : Math.random().toString(36).slice(2, 8).toUpperCase();
  return `${prefix}-${id}`;
};

export { formatUsd };

/* ----------------------------------------------------------------------------
 * Normalizers — ensure new-shape defaults when reading legacy data.
 * ------------------------------------------------------------------------- */

/** Normalize a main scope line item (description, qty, unit, rate, HOP flag). */
/* ----------------------------------------------------------------------------
 * Provenance (`basis`) — where a drafted price came from.
 *
 * INTERNAL ONLY. Never printed on the client's proposal; it exists so the
 * contractor can audit a number and so the accumulated history is gradeable
 * later (docs/pq_draft_proposal_spec.md §4, §7).
 *
 * `tier` is computed SERVER-SIDE by pq-chat against the real source row and is
 * never written by the model — a confidence badge a model can set for itself
 * is one it can talk up. Anything arriving here is already graded.
 *
 *   A  solid       real per-unit price, recent
 *   B  stale       real comp, aging
 *   C  thin        line total / very old / loose match — placeholder
 *   Y  your figure the contractor stated it (trustworthy, but not a comp)
 *
 * Optional by design: every item written before this existed has no `basis`,
 * and every reader must cope. Only attached when present so untouched
 * estimates serialize byte-identically.
 * ------------------------------------------------------------------------- */
const BASIS_TIERS = ['A', 'B', 'C', 'Y'];
// Local, not the module-level `str` — that one is declared further down the
// file and this must not depend on declaration order.
const basisStr = (v, max) => (typeof v === 'string' ? v : '').slice(0, max);

export const normalizeBasis = (basis) => {
  if (!basis || typeof basis !== 'object') return null;
  const tier = BASIS_TIERS.includes(basis.tier) ? basis.tier : null;
  // No tier = not graded = not provenance. Drop it rather than store a
  // half-record that later reads as "sourced".
  if (!tier) return null;
  return {
    tier,
    kind: basisStr(basis.kind, 24) || 'unknown',
    ref: basisStr(basis.ref, 80),
    store: basisStr(basis.store, 120),
    captured: basisStr(basis.captured, 10),
    match: ['exact', 'close', 'loose'].includes(basis.match) ? basis.match : 'close',
    detail: basisStr(basis.detail, 240),
    job: basisStr(basis.job, 120),
  };
};

export const normalizeItem = (item) => {
  const basis = normalizeBasis(item?.basis);
  return {
    id: item?.id || genId('LI'),
    desc: typeof item?.desc === 'string' ? item.desc : '',
    qty: Number(item?.qty ?? 1) || 0,
    unit: typeof item?.unit === 'string' && item.unit ? item.unit : 'ea',
    rate: Number(item?.rate ?? 0) || 0,
    // Phase 15: single HOP flag. Legacy purchase_type='HOP' is migrated to ho_pick=true.
    ho_pick: !!item?.ho_pick || item?.purchase_type === 'HOP',
    ...(basis ? { basis } : {}),
  };
};

/** Normalize a simple sub-row used in materials/allowances lists. */
export const normalizeSimpleRow = (row, prefix = 'LI') => ({
  id: row?.id || genId(prefix),
  desc: typeof row?.desc === 'string' ? row.desc : '',
  qty: Number(row?.qty ?? 1) || 0,
  unit: typeof row?.unit === 'string' && row.unit ? row.unit : 'ea',
  rate: Number(row?.rate ?? 0) || 0,
});

/* ----------------------------------------------------------------------------
 * Rich content blocks (the "open-doc" scope editor).
 *
 * Each scope section may carry a `blocks[]` array of written content that
 * renders in the client proposal ABOVE the section's line-item bullets. This
 * is what turns the Scope-Centric layout into a document editor instead of a
 * single notes box. Blocks live inside the section JSON (which already
 * serializes into the estimate `notes` blob), so no schema change is needed.
 *
 * Types:
 *   heading    — a sub-heading within the section
 *   paragraph  — a run of prose (whitespace preserved)
 *   bullets    — a titled bullet list; variant 'excluded' renders in the
 *                red "Not included / Excluded" treatment
 *   callout    — a boxed note; tone 'included' (green), 'info', or 'warning'
 *   caveat     — a single red italic disclaimer line
 * ------------------------------------------------------------------------- */
const BLOCK_TYPES = [
  'heading',
  'paragraph',
  'bullets',
  'callout',
  'caveat',
  'table',
  'image',
  'chart',
];
const str = (v) => (typeof v === 'string' ? v : '');

// Coerce a table's rows to a rectangular grid matching the column count.
const normalizeTableRows = (rows, colCount) => {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((row) => {
    const cells = Array.isArray(row) ? row.map(str) : [];
    const out = cells.slice(0, colCount);
    while (out.length < colCount) out.push('');
    return out;
  });
};

export const normalizeBlock = (block) => {
  const type = BLOCK_TYPES.includes(block?.type) ? block.type : 'paragraph';
  const base = { id: block?.id || genId('BLK'), type };
  switch (type) {
    case 'heading':
      return { ...base, text: str(block?.text) };
    case 'bullets':
      return {
        ...base,
        title: str(block?.title),
        variant: block?.variant === 'excluded' ? 'excluded' : 'default',
        items: Array.isArray(block?.items) ? block.items.map(str) : [],
      };
    case 'callout':
      return {
        ...base,
        tone: ['included', 'info', 'warning'].includes(block?.tone)
          ? block.tone
          : 'info',
        title: str(block?.title),
        text: str(block?.text),
      };
    case 'caveat':
      return { ...base, text: str(block?.text) };
    case 'image':
      return {
        ...base,
        url: str(block?.url), // public/storage URL rendered in the proposal
        path: str(block?.path), // storage path (for cleanup on delete)
        caption: str(block?.caption),
        // 'contain' shows the whole photo; 'cover' fills a fixed band.
        fit: block?.fit === 'cover' ? 'cover' : 'contain',
      };
    case 'chart': {
      const data = Array.isArray(block?.data)
        ? block.data.map((d) => ({
            label: str(d?.label),
            value: Number(d?.value) || 0,
          }))
        : [];
      return {
        ...base,
        chartType: ['bar', 'line', 'donut'].includes(block?.chartType)
          ? block.chartType
          : 'bar',
        title: str(block?.title),
        // Optional formatting: 'money' prefixes $ on axis/labels.
        valueFormat: block?.valueFormat === 'money' ? 'money' : 'plain',
        data,
      };
    }
    case 'table': {
      const columns =
        Array.isArray(block?.columns) && block.columns.length > 0
          ? block.columns.map(str)
          : ['Item', 'Amount'];
      return {
        ...base,
        columns,
        rows: normalizeTableRows(block?.rows, columns.length),
        // When true, the last column right-aligns (money / quantities).
        numericLast: block?.numericLast !== false,
      };
    }
    case 'paragraph':
    default:
      return { ...base, text: str(block?.text) };
  }
};

/** Default shape for a freshly-added block of a given type. */
export const blankBlock = (type) => {
  if (type === 'table') {
    return normalizeBlock({
      type: 'table',
      columns: ['Item', 'Amount'],
      rows: [['', ''], ['', '']],
    });
  }
  if (type === 'chart') {
    return normalizeBlock({
      type: 'chart',
      chartType: 'bar',
      title: '',
      valueFormat: 'plain',
      data: [
        { label: 'Phase 1', value: 0 },
        { label: 'Phase 2', value: 0 },
      ],
    });
  }
  return normalizeBlock({ type });
};

/**
 * Normalize a section.
 * Backwards compatibility:
 *   - Legacy `is_allowance=true` items remain in `items[]` (no automatic split).
 *     The legacy allowance metadata is dropped silently. User can manually move.
 *   - Legacy `purchase_type` is mapped to `ho_pick` flag.
 *   - Missing materials/allowances/bottom_line fields default to safe values.
 */
export const normalizeSection = (section) => {
  const rawItems = Array.isArray(section?.items) ? section.items : [];
  return {
    id: section?.id || genId('SEC'),
    name: section?.name ?? 'Section',
    // Scope-layout grouping: 'base' rolls into "Total Base Cost", 'allowance'
    // into "Plus Allowances". Defaults to base for all existing sections.
    kind: section?.kind === 'allowance' ? 'allowance' : 'base',
    notes: typeof section?.notes === 'string' ? section.notes : '',
    // Rich "open-doc" content blocks (Scope layout). Empty for itemized/legacy.
    blocks: Array.isArray(section?.blocks)
      ? section.blocks.map(normalizeBlock)
      : [],
    items: rawItems.map(normalizeItem),
    materials: Array.isArray(section?.materials)
      ? section.materials.map((r) => normalizeSimpleRow(r, 'MAT'))
      : [],
    materials_notes:
      typeof section?.materials_notes === 'string' ? section.materials_notes : '',
    allowances: Array.isArray(section?.allowances)
      ? section.allowances.map((r) => normalizeSimpleRow(r, 'ALW'))
      : [],
    allowances_notes:
      typeof section?.allowances_notes === 'string'
        ? section.allowances_notes
        : '',
    bottom_line_mode: !!section?.bottom_line_mode,
    bottom_line_total:
      section?.bottom_line_total === null ||
      section?.bottom_line_total === undefined ||
      section?.bottom_line_total === ''
        ? null
        : Number(section.bottom_line_total) || 0,
  };
};

const normalizeSections = (sections) =>
  Array.isArray(sections) ? sections.map(normalizeSection) : [];

/* ----------------------------------------------------------------------------
 * Project Overview — Phase 14 block, retained
 * ------------------------------------------------------------------------- */
export const buildBlankOverview = () => ({
  description: '',
  scope_summary: '',
  address: '',
  est_start: '',
  est_end: '',
  special_conditions: '',
});

/* ----------------------------------------------------------------------------
 * Time & Material agreement terms (layout === 'tm'). Stored in the estimate
 * blob — no schema change. Mirrors K.O.'s real T&M agreement document.
 * ------------------------------------------------------------------------- */
export const buildBlankTmTerms = (company = null) => {
  const c = company || {};
  return {
    deposit: '', // retainer / deposit required (text so "$4,500" or notes work)
    hourly_rate: '', // e.g. "120"
    material_markup_pct: '', // e.g. "25"
    submission_terms:
      'A record of hours worked is submitted to the homeowner on the Friday of each week worked, along with receipts for materials purchased.',
    payment_terms:
      'Payment is due to the Contractor by the next day of services rendered once the deposit / retainer has been met.',
    start_date: '',
    finish_date: '',
    notes: typeof c.default_tm_notes === 'string' ? c.default_tm_notes : '',
  };
};

const normalizeTmTerms = (tm) => {
  const s = tm && typeof tm === 'object' ? tm : {};
  const str2 = (v) => (typeof v === 'string' ? v : v == null ? '' : String(v));
  return {
    deposit: str2(s.deposit),
    hourly_rate: str2(s.hourly_rate),
    material_markup_pct: str2(s.material_markup_pct),
    submission_terms: str2(s.submission_terms),
    payment_terms: str2(s.payment_terms),
    start_date: str2(s.start_date),
    finish_date: str2(s.finish_date),
    notes: str2(s.notes),
  };
};

const normalizeOverview = (overview) => {
  const o = overview && typeof overview === 'object' ? overview : {};
  return {
    description: typeof o.description === 'string' ? o.description : '',
    scope_summary: typeof o.scope_summary === 'string' ? o.scope_summary : '',
    address: typeof o.address === 'string' ? o.address : '',
    est_start: typeof o.est_start === 'string' ? o.est_start : '',
    est_end: typeof o.est_end === 'string' ? o.est_end : '',
    special_conditions:
      typeof o.special_conditions === 'string' ? o.special_conditions : '',
  };
};

/* ----------------------------------------------------------------------------
 * Totals computation
 * ------------------------------------------------------------------------- */
const lineTotal = (row) =>
  (Number(row?.qty) || 0) * (Number(row?.rate) || 0);

/** Per-row computed total (used by UI for any row type). */
export const sectionItemTotal = (row) => lineTotal(row);

/**
 * Per-section totals.
 *
 *   section_items_total       — main scope items
 *   section_materials_total   — materials sub-rows
 *   section_allowances_total  — allowances sub-rows (INFORMATIONAL only)
 *   section_computed_total    — items + materials (contract amount)
 *   section_display_total     — bottom_line_mode ? (override ?? computed) : computed
 *   section_hop_total         — sum of items where ho_pick=true (informational)
 */
export const computeSectionTotals = (section) => {
  const s = section || {};
  const items = Array.isArray(s.items) ? s.items : [];
  const materials = Array.isArray(s.materials) ? s.materials : [];
  const allowances = Array.isArray(s.allowances) ? s.allowances : [];

  let itemsTotal = 0;
  let hopTotal = 0;
  for (const raw of items) {
    const it = normalizeItem(raw);
    const lt = lineTotal(it);
    itemsTotal += lt;
    if (it.ho_pick) hopTotal += lt;
  }
  const materialsTotal = materials.reduce((acc, r) => acc + lineTotal(r), 0);
  const allowancesTotal = allowances.reduce((acc, r) => acc + lineTotal(r), 0);

  const computed = itemsTotal + materialsTotal;
  const isBottomLine = !!s.bottom_line_mode;
  const override =
    typeof s.bottom_line_total === 'number' && !Number.isNaN(s.bottom_line_total)
      ? s.bottom_line_total
      : null;
  const displayTotal = isBottomLine
    ? override !== null
      ? override
      : computed
    : computed;

  return {
    section_items_total: itemsTotal,
    section_hop_total: hopTotal,
    section_materials_total: materialsTotal,
    section_allowances_total: allowancesTotal,
    section_computed_total: computed,
    section_display_total: displayTotal,
    bottom_line_mode: isBottomLine,
    bottom_line_total: override,
  };
};

/** Backward-compatible alias — returns the section's display total. */
export const sectionSubtotal = (section) =>
  computeSectionTotals(section).section_display_total;

/**
 * Aggregate estimate totals.
 */
export const computeEstimateTotals = (estimate) => {
  const sections = Array.isArray(estimate?.sections) ? estimate.sections : [];
  let displayTotal = 0;
  let computedTotal = 0;
  let itemsTotal = 0;
  let materialsTotal = 0;
  let allowancesTotal = 0;
  let hopTotal = 0;
  let bottomLineCount = 0;
  // Scope-layout split: base sections vs allowance sections (by section.kind).
  let baseSectionsTotal = 0;
  let allowanceSectionsTotal = 0;

  for (const s of sections) {
    const t = computeSectionTotals(s);
    displayTotal += t.section_display_total;
    computedTotal += t.section_computed_total;
    itemsTotal += t.section_items_total;
    materialsTotal += t.section_materials_total;
    allowancesTotal += t.section_allowances_total;
    hopTotal += t.section_hop_total;
    if (t.bottom_line_mode) bottomLineCount += 1;
    if (s?.kind === 'allowance') allowanceSectionsTotal += t.section_display_total;
    else baseSectionsTotal += t.section_display_total;
  }

  const round2 = (n) => Math.round(n * 100) / 100;
  const subtotal = round2(displayTotal);
  const items_subtotal = round2(itemsTotal);
  const materials_subtotal = round2(materialsTotal);
  const allowances_total = round2(allowancesTotal);
  const hop_total = round2(hopTotal);
  const computed_subtotal = round2(computedTotal);

  const taxRate = Math.max(0, Number(estimate?.tax_rate) || 0);
  const tax = round2(subtotal * (taxRate / 100));
  const total = round2(subtotal + tax);

  const schedule = (Array.isArray(estimate?.payment_schedule)
    ? estimate.payment_schedule
    : []
  ).map((row) => {
    const pct = Math.max(0, Math.min(100, Number(row?.percent) || 0));
    return {
      ...row,
      percent: pct,
      amount: round2(total * (pct / 100)),
    };
  });
  const totalPercent = schedule.reduce((s, r) => s + (Number(r?.percent) || 0), 0);

  // Global bottom-line state flags (derived) — useful for the global toggle UI.
  const all_bottom_line = sections.length > 0 && bottomLineCount === sections.length;
  const any_bottom_line = bottomLineCount > 0;

  return {
    subtotal,
    items_subtotal,
    materials_subtotal,
    allowances_total,
    // Scope-layout base/allowance split (by section.kind). base + allowance
    // === subtotal, since every section is one or the other.
    base_sections_total: round2(baseSectionsTotal),
    allowance_sections_total: round2(allowanceSectionsTotal),
    hop_total,
    computed_subtotal,
    tax,
    total,
    payment_schedule: schedule,
    totalPercent,
    all_bottom_line,
    any_bottom_line,
    bottom_line_section_count: bottomLineCount,
    section_count: sections.length,
  };
};

/* ----------------------------------------------------------------------------
 * Default scaffolds
 * ------------------------------------------------------------------------- */
// `t` is an optional react-i18next translator. When provided, seeded labels
// for new estimates render in the active language. When omitted (or a key
// is missing), the function falls back to the canonical English defaults
// so existing logic and tests stay intact.
/**
 * Compose a freeform address string from structured project-address parts.
 * Used to keep overview.address inside the notes JSON blob in sync with the
 * 4 real columns added in migration 013 so older readers still work.
 */
export const composeProjectAddress = ({
  project_address_street,
  project_address_city,
  project_address_state,
  project_address_zip,
} = {}) => {
  const street = (project_address_street || '').trim();
  const city   = (project_address_city   || '').trim();
  const state  = (project_address_state  || '').trim();
  const zip    = (project_address_zip    || '').trim();
  const tail = [city, [state, zip].filter(Boolean).join(' ').trim()]
    .filter(Boolean)
    .join(', ');
  return [street, tail].filter(Boolean).join(', ');
};

// Parse a pay-schedule preset like "40/30/30" into labeled payment rows, so a
// new estimate honors the company's Default pay schedule. Falls back to the
// standard 40/30/30 (Deposit/Milestone/Final) for presets that aren't a clean
// percentage split (e.g. "Net 30").
const buildDefaultPaymentSchedule = (preset, tr) => {
  const labelDeposit = tr('estimates.payment_schedule.label_deposit', 'Deposit');
  const labelMilestone = tr('estimates.payment_schedule.label_milestone', 'Milestone');
  const labelFinal = tr('estimates.payment_schedule.label_final', 'Final');
  const dueSigning = tr('estimates.payment_schedule.due_at_signing', 'At signing');
  const dueMid = tr('estimates.payment_schedule.due_mid_project', 'Mid-project');
  const dueDone = tr('estimates.payment_schedule.due_project_completion', 'Project completion');

  const parts = String(preset || '')
    .split('/')
    .map((p) => Number(String(p).trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
  const sum = parts.reduce((a, b) => a + b, 0);
  const clean = (parts.length === 2 || parts.length === 3) && Math.abs(sum - 100) <= 1;

  const rows =
    clean && parts.length === 3
      ? [
          { label: labelDeposit, percent: parts[0], due: dueSigning },
          { label: labelMilestone, percent: parts[1], due: dueMid },
          { label: labelFinal, percent: parts[2], due: dueDone },
        ]
      : clean && parts.length === 2
      ? [
          { label: labelDeposit, percent: parts[0], due: dueSigning },
          { label: labelFinal, percent: parts[1], due: dueDone },
        ]
      : [
          { label: labelDeposit, percent: 40, due: dueSigning },
          { label: labelMilestone, percent: 30, due: dueMid },
          { label: labelFinal, percent: 30, due: dueDone },
        ];

  return rows.map((r) => ({ id: genId('PS'), ...r }));
};

export const buildBlankEstimate = ({
  clientId = null,
  clientName = '',
  t = null,
  company = null,
  defaults = null,
} = {}) => {
  const tr = (key, fallback) => {
    if (typeof t !== 'function') return fallback;
    const v = t(key, { defaultValue: fallback });
    return v || fallback;
  };
  // Safe fallback so a missing/undefined company profile never throws.
  const c = company ?? {};
  // Company Defaults (settings key 'defaults') seed a new estimate so the
  // Default tax % / pay schedule actually apply instead of being orphaned.
  const d = defaults ?? {};
  const defaultTaxRate = Math.max(0, Number(d.default_tax_pct) || 0);
  return {
    id: null,
    project_name: '',
    title: '',
    client_id: clientId,
    client_name: clientName,
    status: 'draft',
    tax_rate: defaultTaxRate,
    // Proposal layout: 'itemized' (line-item tables) or 'scope' (narrative
    // scope-of-work). Defaults to itemized so existing estimates are unchanged.
    layout: 'itemized',
    overview: buildBlankOverview(),
    tm_terms: buildBlankTmTerms(company),
    // Phase 26 — proposal terms columns. Reusable clauses pre-filled from
    // company defaults when available.
    prepared_by: '',
    proposal_valid_for: '',
    estimated_duration: '',
    exclusions: '',
    client_responsibilities: '',
    company_responsibilities: '',
    warranty: c.default_warranty || '',
    special_clauses: c.default_special_clauses || '',
    change_order_clause: c.default_change_order_clause || '',
    // Phase 27 — lifecycle (migration 014).
    outcome_reason: '',
    won_at: null,
    lost_at: null,
    // Phase 26 step 3 — structured project address (migration 013).
    project_address_street: '',
    project_address_city: '',
    project_address_state: '',
    project_address_zip: '',
    sections: [
      normalizeSection({
        id: genId('SEC'),
        name: tr('estimates.section.scope_default', 'Scope'),
        items: [
          normalizeItem({
            id: genId('LI'),
            desc: '',
            qty: 1,
            unit: 'ea',
            rate: 0,
          }),
        ],
      }),
    ],
    payment_schedule: buildDefaultPaymentSchedule(d.pay_schedule, tr),
    notes_text: '',
    // Contract pages (render-only, print-ready). Opt-in so existing/short
    // proposals are unchanged. `legal_state` selects the state legal pack;
    // '' auto-resolves from the project address state at render time.
    include_acceptance: false,
    include_legal: false,
    legal_state: '',
  };
};

/* ----------------------------------------------------------------------------
 * Hydrate / Dehydrate
 * ------------------------------------------------------------------------- */
export const hydrateEstimate = (row) => {
  if (!row) return null;
  const blob = decodeBlob(row.notes) || {};
  return {
    id: row.id,
    title: row.title || blob.project_name || '',
    project_name: blob.project_name || row.title || '',
    client_id: row.client_id || blob.client_id || null,
    client_name: blob.client_name || '',
    status: normalizeStatus(row.status || 'draft'),
    tax_rate: typeof blob.tax_rate === 'number' ? blob.tax_rate : 0,
    layout: ['scope', 'tm'].includes(blob.layout) ? blob.layout : 'itemized',
    overview: normalizeOverview(blob.overview),
    tm_terms: normalizeTmTerms(blob.tm_terms),
    sections: normalizeSections(blob.sections),
    payment_schedule: Array.isArray(blob.payment_schedule)
      ? blob.payment_schedule
      : [],
    notes_text: typeof blob.notes_text === 'string' ? blob.notes_text : '',
    include_acceptance: !!blob.include_acceptance,
    include_legal: !!blob.include_legal,
    legal_state: typeof blob.legal_state === 'string' ? blob.legal_state : '',
    subtotal: Number(row.subtotal) || 0,
    tax: Number(row.tax) || 0,
    total: Number(row.total) || 0,
    job_id: row.job_id || null,
    sent_at: row.sent_at || null,
    approved_at: row.approved_at || null,
    // Phase 27 — lifecycle (migration 014). outcome_reason is free-editable
    // text; the dropdown options live in @/lib/estimateLifecycle.
    won_at: row.won_at || null,
    lost_at: row.lost_at || null,
    outcome_reason: row.outcome_reason || '',
    created_at: row.created_at || null,
    // Phase 26 — proposal terms (real columns). Default to '' to keep React
    // controlled inputs happy.
    prepared_by: row.prepared_by || '',
    proposal_valid_for: row.proposal_valid_for || '',
    estimated_duration: row.estimated_duration || '',
    exclusions: row.exclusions || '',
    client_responsibilities: row.client_responsibilities || '',
    company_responsibilities: row.company_responsibilities || '',
    change_order_clause: row.change_order_clause || '',
    warranty: row.warranty || '',
    special_clauses: row.special_clauses || '',
    // Phase 26 step 3 — structured project address (migration 013).
    project_address_street: row.project_address_street || '',
    project_address_city:   row.project_address_city   || '',
    project_address_state:  row.project_address_state  || '',
    project_address_zip:    row.project_address_zip    || '',
  };
};

const dehydrateEstimate = (estimate) => {
  const totals = computeEstimateTotals(estimate);
  // Phase 26 step 3 — keep the legacy overview.address (inside JSON blob)
  // in sync with the new structured project_address_* columns so existing
  // readers continue to work.
  const composedProjectAddress = composeProjectAddress(estimate);
  const overviewOut = normalizeOverview(estimate.overview);
  if (composedProjectAddress) {
    overviewOut.address = composedProjectAddress;
  }
  const blob = {
    project_name: estimate.project_name || estimate.title || '',
    client_name: estimate.client_name || '',
    client_id: estimate.client_id || null,
    tax_rate: Math.max(0, Number(estimate.tax_rate) || 0),
    layout: ['scope', 'tm'].includes(estimate.layout)
      ? estimate.layout
      : 'itemized',
    overview: overviewOut,
    tm_terms: normalizeTmTerms(estimate.tm_terms),
    sections: normalizeSections(estimate.sections),
    payment_schedule: totals.payment_schedule,
    notes_text: estimate.notes_text || '',
    include_acceptance: !!estimate.include_acceptance,
    include_legal: !!estimate.include_legal,
    legal_state: estimate.legal_state || '',
  };
  return {
    title: estimate.title || estimate.project_name || 'Untitled estimate',
    client_id: estimate.client_id || null,
    job_id: estimate.job_id || null,
    status: normalizeStatus(estimate.status || 'draft'),
    subtotal: totals.subtotal,
    tax: totals.tax,
    total: totals.total,
    notes: encodeBlob(blob),
    // Phase 27 — lifecycle (migration 014). Outcome reason persists for both
    // 'won' and 'lost'. Stamp won_at / lost_at on the matching status, and
    // clear them when status moves away from that terminal state so the
    // timeline stays truthful for back-and-forth status changes.
    outcome_reason:
      normalizeStatus(estimate.status) === 'won' ||
      normalizeStatus(estimate.status) === 'lost'
        ? (estimate.outcome_reason || null)
        : null,
    won_at:
      normalizeStatus(estimate.status) === 'won'
        ? (estimate.won_at || new Date().toISOString())
        : null,
    lost_at:
      normalizeStatus(estimate.status) === 'lost'
        ? (estimate.lost_at || new Date().toISOString())
        : null,
    // Phase 26 — proposal terms (real columns; NULL when blank to keep
    // the DB tidy rather than empty strings).
    prepared_by:              estimate.prepared_by              || null,
    proposal_valid_for:       estimate.proposal_valid_for       || null,
    estimated_duration:       estimate.estimated_duration       || null,
    exclusions:               estimate.exclusions               || null,
    client_responsibilities:  estimate.client_responsibilities  || null,
    company_responsibilities: estimate.company_responsibilities || null,
    change_order_clause:      estimate.change_order_clause      || null,
    warranty:                 estimate.warranty                 || null,
    special_clauses:          estimate.special_clauses          || null,
    // Phase 26 step 3 — structured project address (migration 013).
    project_address_street:   estimate.project_address_street   || null,
    project_address_city:     estimate.project_address_city     || null,
    project_address_state:    estimate.project_address_state    || null,
    project_address_zip:      estimate.project_address_zip      || null,
  };
};

const EST_FIELDS =
  'id,job_id,client_id,title,status,subtotal,tax,total,notes,sent_at,approved_at,created_at,' +
  'won_at,lost_at,outcome_reason,' +
  'prepared_by,proposal_valid_for,estimated_duration,exclusions,' +
  'client_responsibilities,company_responsibilities,change_order_clause,' +
  'warranty,special_clauses,' +
  'project_address_street,project_address_city,project_address_state,project_address_zip';

/* ----------------------------------------------------------------------------
 * CRUD
 * ------------------------------------------------------------------------- */
export const fetchEstimates = async () => {
  const { data, error } = await supabase
    .from('estimates')
    .select(EST_FIELDS)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(hydrateEstimate);
};

export const fetchEstimate = async (id) => {
  const { data, error } = await supabase
    .from('estimates')
    .select(EST_FIELDS)
    .eq('id', id)
    .single();
  if (error) throw error;
  return hydrateEstimate(data);
};

export const fetchEstimateByJob = async (jobId) => {
  const { data, error } = await supabase
    .from('estimates')
    .select(EST_FIELDS)
    .eq('job_id', jobId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data ? hydrateEstimate(data) : null;
};

export const saveEstimate = async (estimate) => {
  const payload = dehydrateEstimate(estimate);
  const isNew = !estimate.id;
  if (estimate.id) {
    const { data, error } = await supabase
      .from('estimates')
      .update(payload)
      .eq('id', estimate.id)
      .select(EST_FIELDS)
      .single();
    if (error) throw error;
    return hydrateEstimate(data);
  }
  const { data, error } = await supabase
    .from('estimates')
    .insert([payload])
    .select(EST_FIELDS)
    .single();
  if (error) throw error;
  const hydrated = hydrateEstimate(data);
  if (isNew) pqi.trackEstimateCreated({ estimate: hydrated, jobId: hydrated.job_id });
  return hydrated;
};

export const deleteEstimate = async (id) => {
  const { error } = await supabase.from('estimates').delete().eq('id', id);
  if (error) throw error;
  return true;
};

/* ----------------------------------------------------------------------------
 * Convert to Job
 *
 * Duplicate guard: converting the same estimate twice — or converting an
 * estimate whose job was already created by hand — must never mint a second
 * job. Before creating anything we (1) follow the estimate's own job_id if it
 * still resolves, then (2) look for an existing non-bucket job with the same
 * name (case-insensitive) or, failing that, the same client. If either is
 * found we link the estimate to it and return { linkedExisting: true } so the
 * UI can say "linked" instead of "created".
 * ------------------------------------------------------------------------- */
const normName = (s) => String(s || '').trim().toLowerCase();

const findExistingJobForEstimate = async (estimate) => {
  // 1) The estimate already points at a job — reuse it if it still exists.
  if (estimate.job_id) {
    const { data } = await supabase
      .from('jobs')
      .select('id,name,client_id,contract,status')
      .eq('id', estimate.job_id)
      .maybeSingle();
    if (data) return data;
  }
  // 2) A job with the same name (the manual-creation case).
  const wanted = normName(estimate.project_name || estimate.title);
  if (wanted) {
    const { data: byName } = await supabase
      .from('jobs')
      .select('id,name,client_id,contract,status')
      .ilike('name', String(estimate.project_name || estimate.title).trim());
    const nameHit = (byName || []).find((j) => normName(j.name) === wanted);
    if (nameHit) return nameHit;
  }
  // 3) An active job for the same client (skip buckets: client_id null).
  if (estimate.client_id) {
    const { data: byClient } = await supabase
      .from('jobs')
      .select('id,name,client_id,contract,status')
      .eq('client_id', estimate.client_id)
      .neq('status', 'closed')
      .order('created_at', { ascending: false })
      .limit(1);
    if ((byClient || []).length > 0) return byClient[0];
  }
  return null;
};

export const convertEstimateToJob = async (estimate, { clients = [] } = {}) => {
  const totals = computeEstimateTotals(estimate);

  const existing = await findExistingJobForEstimate(estimate);
  if (existing) {
    if (estimate.id) {
      await supabase
        .from('estimates')
        .update({
          job_id: existing.id,
          status: 'won',
          won_at: new Date().toISOString(),
        })
        .eq('id', estimate.id);
    }
    return { job: existing, total: totals.total, linkedExisting: true };
  }

  const linkedClient = clients.find((c) => c?.id === estimate.client_id);
  const overview = normalizeOverview(estimate?.overview);
  // A T&M agreement converts to a T&M job: no contract value (the deposit is
  // a retainer, not a price) and no auto 40/30/30 invoice schedule — billing
  // follows the agreement's weekly record instead. Storing a T&M job as
  // 'fixed' made Job Tracker read the deposit as a contract and flag normal
  // work-ahead-of-billing as a loss (the Jennison lesson, Sep 5).
  const isTM = estimate?.layout === 'tm';
  const created = await createJob({
    name: estimate.project_name || estimate.title || 'Estimate-derived job',
    client: linkedClient?.name || estimate.client_name || '',
    client_id: estimate.client_id || null,
    address: overview.address || linkedClient?.address || '',
    contract: isTM ? 0 : totals.total,
    job_type: isTM ? 'tm' : 'fixed',
    started: overview.est_start || 'TBD',
    est_end: overview.est_end || 'TBD',
  });

  if (estimate.id) {
    // Phase 27 — convert auto-marks the estimate as Won and stamps won_at.
    // approved_at is intentionally left alone (column retained for back-compat
    // per migration 014; we no longer write it from the app).
    await supabase
      .from('estimates')
      .update({
        job_id: created.id,
        status: 'won',
        won_at: new Date().toISOString(),
      })
      .eq('id', estimate.id);
  }

  return { job: created, total: totals.total };
};

/* ----------------------------------------------------------------------------
 * PQ Chat Phase C — apply a PQ-drafted proposal to the OPEN estimate.
 *
 * Proposals come back from the pq-chat edge function as structured cards; the
 * server never writes them. Applying happens here, in the builder's local
 * state, through the same immutable shapes manual edits use — the change then
 * rides the contractor's normal Save. Kinds:
 *   scope_item — bullet in the named section (appends to its trailing bullets
 *                block when one exists, else adds a new one)
 *   exclusion  — line appended to the estimate's `exclusions` term field
 *   line_item  — priced row in the named section (qty 1 × amount)
 *   callout    — highlighted note block in the named section
 * A section name that doesn't exist is created (case-insensitive match).
 * ------------------------------------------------------------------------- */
// Normalized compare for duplicate detection — case/whitespace-insensitive.
const normProposalText = (s) =>
  String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');

/* ----------------------------------------------------------------------------
 * Near-duplicate detection (2026-10-01). Exact matching let reworded repeats
 * through — "Removal of loose site items…" and "Removal AND DISPOSAL of loose
 * site items…" both landed in the Johnson estimate. Two texts are the same
 * item when their NUMBERS match exactly (so a 16'x8' door is never mistaken
 * for a 12'x14' one, or 16 pc for 18 pc) and their words mostly overlap, or
 * one is a fuller version of the other.
 * ------------------------------------------------------------------------- */
const DUP_FILLER = new Set(['a', 'an', 'the', 'and', 'or', 'of', 'to', 'for', 'as', 'with', 'on', 'in', 'at', 'by', 'any', 'all']);

const dupTokens = (s) => {
  const raw = String(s || '').toLowerCase().replace(/[^a-z0-9.]+/g, ' ').trim();
  const tokens = raw ? raw.split(' ') : [];
  const numbers = tokens.filter((t) => /\d/.test(t)).sort();
  const words = new Set(tokens.filter((t) => !/\d/.test(t) && !DUP_FILLER.has(t) && t.length > 1));
  return { numbers, words };
};

export const isNearDuplicateText = (a, b) => {
  if (normProposalText(a) === normProposalText(b)) return true;
  const A = dupTokens(a);
  const B = dupTokens(b);
  if (A.numbers.join(' ') !== B.numbers.join(' ')) return false;
  if (A.words.size === 0 || B.words.size === 0) return false;
  let inter = 0;
  A.words.forEach((w) => {
    if (B.words.has(w)) inter += 1;
  });
  const union = A.words.size + B.words.size - inter;
  if (inter / union >= 0.8) return true;
  // One is a fuller version of the other (the shorter's words all appear in
  // the longer). Needs enough words to mean something.
  const smaller = Math.min(A.words.size, B.words.size);
  return smaller >= 5 && inter === smaller;
};

const GENERIC_CALLOUT_TITLES = new Set(['site conditions to note', 'note', 'notes', '']);

// A callout repeats an existing one when its text is a near-duplicate, or it
// carries the same specific title (PQ re-adding "Structural tie-in pending
// engineering" after the contractor edited the first one's text).
const isDuplicateCallout = (block, title, text) =>
  block?.type === 'callout' &&
  (isNearDuplicateText(block.text, text) ||
    (!GENERIC_CALLOUT_TITLES.has(normProposalText(title)) &&
      normProposalText(block.title) === normProposalText(title)));

/* ----------------------------------------------------------------------------
 * Which existing section does a proposal mean? (2026-10-01) By ID first — PQ
 * reads section ids from the document and passes them through — then by exact
 * name, then tolerating a rename: the contractor renamed "PHASE II : Concrete"
 * to "PHASE II : Concrete (Weaver excavatine)", PQ kept asking for the old
 * name, and every apply created a duplicate section. A rename match needs the
 * shorter name's words to lead the longer one's word-for-word (so "Phase I"
 * never matches "Phase II"), with at least two words.
 * Returns the index, or -1 when a new section really is wanted.
 * ------------------------------------------------------------------------- */
const sectionWords = (s) =>
  String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(' ').filter(Boolean);

export const resolveSectionIndex = (sections, wantedName, wantedId) => {
  const list = Array.isArray(sections) ? sections : [];
  if (wantedId) {
    const byId = list.findIndex((s) => s?.id === wantedId);
    if (byId !== -1) return byId;
  }
  const name = String(wantedName || '').trim().toLowerCase();
  const exact = list.findIndex((s) => String(s?.name || '').trim().toLowerCase() === name);
  if (exact !== -1) return exact;
  const want = sectionWords(wantedName);
  if (want.length < 2) return -1;
  return list.findIndex((s) => {
    const have = sectionWords(s?.name);
    const [shorter, longer] = have.length <= want.length ? [have, want] : [want, have];
    return shorter.length >= 2 && shorter.every((w, i) => longer[i] === w);
  });
};

/* ----------------------------------------------------------------------------
 * applyDraftToEstimate — apply a whole PQ-drafted proposal in one pass.
 *
 * The card-at-a-time path (applyProposalToEstimate, below) is still how single
 * suggestions land. This is the `draft_proposal` path: one document, one Apply.
 *
 * THE SAFETY RULE: a draft never overwrites work that is already there. It
 * fills empty fields and appends to lists, and skips anything already
 * populated. Apply can add to the estimate; it can never destroy it. Whatever
 * was skipped is reported back so the review card can say so out loud.
 *
 * Payment schedule is deliberately NOT applied in this version even though PQ
 * drafts it — it moves money, and silently replacing a contractor's draw
 * schedule is not a thing to do behind a single button. It is shown on the
 * review card to copy in by hand (spec §5, revisit with an explicit toggle).
 *
 * Returns { estimate, applied, skipped } — never mutates the input.
 * ------------------------------------------------------------------------- */
export const applyDraftToEstimate = (estimate, draft) => {
  const applied = [];
  const skipped = [];
  if (!estimate || !draft) return { estimate, applied, skipped };

  let next = { ...estimate };
  // Read BEFORE the draft's lines land: is this a fresh estimate with no
  // prices yet? Only then may the draft's payment schedule replace the
  // seeded default — never a schedule he's already set against real numbers.
  const freshEstimate = computeEstimateTotals(estimate).subtotal === 0;

  // --- Project name: fill only when empty -------------------------------
  const draftTitle = String(draft.title || '').trim();
  if (draftTitle && !String(next.project_name || '').trim()) {
    next = { ...next, project_name: draftTitle, title: draftTitle };
    applied.push('Project name');
  }

  // --- Overview: fill only when empty -----------------------------------
  const draftOverview = String(draft.overview || '').trim();
  if (draftOverview) {
    const currentOverview = String(next.overview?.description || '').trim();
    if (currentOverview) {
      skipped.push('Overview (already written)');
    } else {
      next = {
        ...next,
        overview: { ...normalizeOverview(next.overview), description: draftOverview },
      };
      applied.push('Overview');
    }
  }

  // --- Sections: scope bullets, priced line items, callouts -------------
  let sections = Array.isArray(next.sections) ? [...next.sections] : [];
  // A fresh estimate is seeded with an empty "Scope" section (one blank
  // line). When a whole draft arrives — e.g. a finished proposal being
  // transferred in — that placeholder would sit empty above it. Drop
  // sections with nothing in them; anything with a word or a number stays.
  const isEmptySection = (sec) =>
    !(sec?.blocks || []).some((b) =>
      (b?.items || []).some((x) => String(x || '').trim()) || String(b?.text || '').trim()
    ) &&
    !(sec?.items || []).some((it) => String(it?.desc || '').trim() || Number(it?.rate)) &&
    !(sec?.materials || []).some((r) => String(r?.desc || '').trim() || Number(r?.rate)) &&
    !(sec?.allowances || []).some((r) => String(r?.desc || '').trim() || Number(r?.rate)) &&
    !String(sec?.notes || '').trim();
  if (freshEstimate && (draft.sections || []).length > 0) {
    sections = sections.filter((sec) => !isEmptySection(sec));
  }
  let bullets = 0;
  let lines = 0;
  let callouts = 0;

  for (const draftSection of Array.isArray(draft.sections) ? draft.sections : []) {
    const wanted = String(draftSection?.name || '').trim() || 'Scope';
    let idx = resolveSectionIndex(sections, wanted, draftSection?.section_id);
    if (idx === -1) {
      sections.push(normalizeSection({ id: genId('SEC'), name: wanted }));
      idx = sections.length - 1;
    }
    const section = { ...sections[idx] };
    const blocks = [...(section.blocks || [])];
    const items = [...(section.items || [])];

    // Scope bullets — one list per section, deduped against the whole section.
    const newBullets = [];
    for (const raw of Array.isArray(draftSection?.scope_items) ? draftSection.scope_items : []) {
      const text = String(raw || '').trim();
      if (!text) continue;
      const exists =
        blocks.some(
          (b) =>
            b?.type === 'bullets' &&
            (b.items || []).some((it) => isNearDuplicateText(it, text))
        ) || newBullets.some((it) => isNearDuplicateText(it, text));
      if (!exists) newBullets.push(text);
    }
    if (newBullets.length > 0) {
      const last = blocks[blocks.length - 1];
      if (last && last.type === 'bullets' && last.variant !== 'excluded') {
        blocks[blocks.length - 1] = { ...last, items: [...last.items, ...newBullets] };
      } else {
        blocks.push(normalizeBlock({ type: 'bullets', items: newBullets }));
      }
      bullets += newBullets.length;
    }

    // Priced line items — `basis` rides along (internal provenance).
    for (const raw of Array.isArray(draftSection?.items) ? draftSection.items : []) {
      const desc = String(raw?.desc || '').trim();
      if (!desc) continue;
      if (items.some((it) => isNearDuplicateText(it?.desc, desc))) continue;
      items.push(
        normalizeItem({
          desc,
          qty: Number(raw?.qty ?? 1) || 1,
          unit: raw?.unit || 'ea',
          // A line the server could not source comes through with no rate and
          // lands as $0 — an obviously unfinished number, which is the point.
          rate: Number(raw?.rate) || 0,
          basis: raw?.basis,
        })
      );
      lines += 1;
    }

    // Callouts — client-facing site-condition boxes.
    for (const raw of Array.isArray(draftSection?.callouts) ? draftSection.callouts : []) {
      const text = String(raw?.text || '').trim();
      if (!text) continue;
      if (blocks.some((b) => isDuplicateCallout(b, raw?.title, text))) continue;
      blocks.push(
        normalizeBlock({
          type: 'callout',
          tone: 'warning',
          title: String(raw?.title || '').trim() || 'Site conditions to note',
          text,
        })
      );
      callouts += 1;
    }

    section.blocks = blocks;
    section.items = items;
    sections[idx] = section;
  }

  if (bullets || lines || callouts) {
    next = { ...next, sections };
    if (bullets) applied.push(`${bullets} scope item${bullets === 1 ? '' : 's'}`);
    if (lines) applied.push(`${lines} line item${lines === 1 ? '' : 's'}`);
    if (callouts) applied.push(`${callouts} callout${callouts === 1 ? '' : 's'}`);
  }

  // --- Append-and-dedupe text lists -------------------------------------
  const appendLines = (field, values, label) => {
    const incoming = (Array.isArray(values) ? values : [])
      .map((v) => String(v || '').trim())
      .filter(Boolean);
    if (incoming.length === 0) return;
    const prev = String(next[field] || '').trim();
    const seen = prev.split('\n').filter((l) => l.trim());
    const add = [];
    for (const line of incoming) {
      if (seen.some((s) => isNearDuplicateText(s, line)) || add.some((a) => isNearDuplicateText(a, line))) continue;
      add.push(line);
    }
    if (add.length === 0) return;
    next = { ...next, [field]: prev ? `${prev}\n${add.join('\n')}` : add.join('\n') };
    applied.push(`${add.length} ${label}${add.length === 1 ? '' : 's'}`);
  };

  appendLines('exclusions', draft.excluded, 'exclusion');
  appendLines('special_clauses', draft.assumptions, 'assumption');

  const draftSchedule = (Array.isArray(draft.payment_schedule) ? draft.payment_schedule : [])
    .filter((p) => String(p?.label || '').trim());
  if (draftSchedule.length > 0) {
    const pctSum = draftSchedule.reduce((n, p) => n + (Number(p?.pct) || 0), 0);
    if (freshEstimate && Math.abs(pctSum - 100) < 0.01) {
      next = {
        ...next,
        payment_schedule: draftSchedule.map((p) => ({
          id: genId('PS'),
          label: String(p.label).trim(),
          percent: Math.max(0, Math.min(100, Number(p.pct) || 0)),
          due: String(p?.due || '').trim(),
        })),
      };
      applied.push(`Payment schedule (${draftSchedule.length} payments)`);
    } else if (!freshEstimate) {
      skipped.push('Payment schedule (this estimate already has prices — kept yours; the draft’s is on the card)');
    } else {
      skipped.push(`Payment schedule (percents add to ${Math.round(pctSum * 100) / 100}%, not 100% — enter it yourself)`);
    }
  }

  return { estimate: next, applied, skipped };
};

export const applyProposalToEstimate = (estimate, proposal) => {
  if (!estimate || !proposal) return estimate;
  // A whole-document draft routes to its own applier; the card path below is
  // for single suggestions.
  if (proposal.kind === 'draft') {
    return applyDraftToEstimate(estimate, proposal).estimate;
  }
  const text = String(proposal.text || '').trim();
  if (!text) return estimate;

  if (proposal.kind === 'exclusion') {
    const prev = String(estimate.exclusions || '').trim();
    // Duplicate guard: PQ re-proposes batches on follow-ups; applying the
    // same exclusion twice must be a no-op (same object back = "skipped").
    if (prev.split('\n').some((l) => isNearDuplicateText(l, text))) {
      return estimate;
    }
    return { ...estimate, exclusions: prev ? `${prev}\n${text}` : text };
  }

  const wanted = String(proposal.section || '').trim() || 'Scope';
  const sections = Array.isArray(estimate.sections) ? [...estimate.sections] : [];
  let idx = resolveSectionIndex(sections, wanted, proposal.section_id);
  if (idx === -1) {
    sections.push(normalizeSection({ id: genId('SEC'), name: wanted }));
    idx = sections.length - 1;
  }
  const section = { ...sections[idx] };

  // Duplicate guard per kind, scanning the WHOLE target section: the same
  // bullet/line/callout applied twice is a no-op (original object returned).
  if (proposal.kind === 'scope_item') {
    const hasBullet = (section.blocks || []).some(
      (b) =>
        b?.type === 'bullets' &&
        (b.items || []).some((it) => isNearDuplicateText(it, text))
    );
    if (hasBullet) return estimate;
  } else if (proposal.kind === 'line_item') {
    const hasItem = (section.items || []).some((it) => isNearDuplicateText(it?.desc, text));
    if (hasItem) return estimate;
  } else if (proposal.kind === 'callout') {
    const hasCallout = (section.blocks || []).some((b) =>
      isDuplicateCallout(b, proposal.title, text)
    );
    if (hasCallout) return estimate;
  }

  if (proposal.kind === 'scope_item') {
    const blocks = [...(section.blocks || [])];
    const last = blocks[blocks.length - 1];
    if (last && last.type === 'bullets' && last.variant !== 'excluded') {
      blocks[blocks.length - 1] = { ...last, items: [...last.items, text] };
    } else {
      blocks.push(normalizeBlock({ type: 'bullets', items: [text] }));
    }
    section.blocks = blocks;
  } else if (proposal.kind === 'callout') {
    section.blocks = [
      ...(section.blocks || []),
      normalizeBlock({
        type: 'callout',
        tone: 'warning',
        title: proposal.title || 'Site conditions to note',
        text,
      }),
    ];
  } else if (proposal.kind === 'line_item') {
    section.items = [
      ...(section.items || []),
      // `basis` rides along so the applied line keeps its provenance — the
      // card showed where the price came from, and the estimate should
      // remember it (internal only, never printed).
      normalizeItem({
        desc: text,
        qty: 1,
        rate: Number(proposal.amount) || 0,
        basis: proposal.basis,
      }),
    ];
  } else {
    return estimate;
  }

  sections[idx] = section;
  return { ...estimate, sections };
};

/**
 * Is this proposal's content ALREADY in the estimate? Derived from the
 * document itself, not from UI state — the duplicate guards above return the
 * ORIGINAL estimate object when applying would be a no-op, so "apply would
 * change nothing" IS the definition of "already in". Used by the chat panel
 * to render "Already in the estimate" instead of a live Apply button after a
 * reload wiped the session-local applied marks (the estimate is the source
 * of truth; the buttons should tell that truth).
 */
export const isProposalInEstimate = (estimate, proposal) => {
  if (!estimate || !proposal) return false;
  if (proposal.kind === 'draft') {
    // A draft is "in" when a re-apply would add nothing new.
    return applyDraftToEstimate(estimate, proposal).applied.length === 0;
  }
  return applyProposalToEstimate(estimate, proposal) === estimate;
};

/* ----------------------------------------------------------------------------
 * Builder helpers (immutable updates)
 * ------------------------------------------------------------------------- */
export const addSection = (estimate, name = 'New section') => ({
  ...estimate,
  sections: [
    ...(estimate.sections || []),
    normalizeSection({ id: genId('SEC'), name }),
  ],
});

export const removeSection = (estimate, sectionId) => ({
  ...estimate,
  sections: (estimate.sections || []).filter((s) => s.id !== sectionId),
});

export const updateSection = (estimate, sectionId, patch) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) =>
    s.id === sectionId ? { ...s, ...patch } : s
  ),
});

/* ---- Content blocks (open-doc scope editor) ----------------------------- */
export const addBlock = (estimate, sectionId, type = 'paragraph') => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) =>
    s.id === sectionId
      ? { ...s, blocks: [...(s.blocks || []), blankBlock(type)] }
      : s
  ),
});

export const updateBlock = (estimate, sectionId, blockId, patch) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) =>
    s.id === sectionId
      ? {
          ...s,
          blocks: (s.blocks || []).map((b) =>
            b.id === blockId ? { ...b, ...patch } : b
          ),
        }
      : s
  ),
});

export const removeBlock = (estimate, sectionId, blockId) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) =>
    s.id === sectionId
      ? { ...s, blocks: (s.blocks || []).filter((b) => b.id !== blockId) }
      : s
  ),
});

/** Move a block up (-1) or down (+1) within its section. */
export const moveBlock = (estimate, sectionId, blockId, dir) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) => {
    if (s.id !== sectionId) return s;
    const blocks = [...(s.blocks || [])];
    const i = blocks.findIndex((b) => b.id === blockId);
    const j = i + (dir < 0 ? -1 : 1);
    if (i < 0 || j < 0 || j >= blocks.length) return s;
    [blocks[i], blocks[j]] = [blocks[j], blocks[i]];
    return { ...s, blocks };
  }),
});

/* ---- Items (main scope) -------------------------------------------------- */
export const addLineItem = (estimate, sectionId, overrides = {}) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) => {
    if (s.id !== sectionId) return s;
    return {
      ...s,
      items: [
        ...(s.items || []),
        normalizeItem({
          id: genId('LI'),
          desc: '',
          qty: 1,
          unit: 'ea',
          rate: 0,
          ...overrides,
        }),
      ],
    };
  }),
});

export const updateLineItem = (estimate, sectionId, itemId, patch) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) => {
    if (s.id !== sectionId) return s;
    return {
      ...s,
      items: (s.items || []).map((it) =>
        it.id === itemId ? { ...it, ...patch } : it
      ),
    };
  }),
});

export const removeLineItem = (estimate, sectionId, itemId) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) => {
    if (s.id !== sectionId) return s;
    return { ...s, items: (s.items || []).filter((it) => it.id !== itemId) };
  }),
});

/* ---- Materials sub-rows -------------------------------------------------- */
export const addMaterialRow = (estimate, sectionId) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) => {
    if (s.id !== sectionId) return s;
    return {
      ...s,
      materials: [
        ...(s.materials || []),
        normalizeSimpleRow({ id: genId('MAT') }, 'MAT'),
      ],
    };
  }),
});

export const updateMaterialRow = (estimate, sectionId, rowId, patch) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) => {
    if (s.id !== sectionId) return s;
    return {
      ...s,
      materials: (s.materials || []).map((r) =>
        r.id === rowId ? { ...r, ...patch } : r
      ),
    };
  }),
});

export const removeMaterialRow = (estimate, sectionId, rowId) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) => {
    if (s.id !== sectionId) return s;
    return { ...s, materials: (s.materials || []).filter((r) => r.id !== rowId) };
  }),
});

/* ---- Allowances sub-rows ------------------------------------------------- */
export const addAllowanceRow = (estimate, sectionId) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) => {
    if (s.id !== sectionId) return s;
    return {
      ...s,
      allowances: [
        ...(s.allowances || []),
        normalizeSimpleRow({ id: genId('ALW') }, 'ALW'),
      ],
    };
  }),
});

export const updateAllowanceRow = (estimate, sectionId, rowId, patch) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) => {
    if (s.id !== sectionId) return s;
    return {
      ...s,
      allowances: (s.allowances || []).map((r) =>
        r.id === rowId ? { ...r, ...patch } : r
      ),
    };
  }),
});

export const removeAllowanceRow = (estimate, sectionId, rowId) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) => {
    if (s.id !== sectionId) return s;
    return {
      ...s,
      allowances: (s.allowances || []).filter((r) => r.id !== rowId),
    };
  }),
});

/* ---- Bottom Line toggles ------------------------------------------------- */
/** Toggle bottom-line mode on a single section. */
export const setSectionBottomLineMode = (estimate, sectionId, on) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) => {
    if (s.id !== sectionId) return s;
    return {
      ...s,
      bottom_line_mode: !!on,
      // When disabling bottom-line, drop the manual override so the computed total returns.
      bottom_line_total: on ? s.bottom_line_total ?? null : null,
    };
  }),
});

/** Set the manual section total (only meaningful while bottom_line_mode=true). */
export const setSectionBottomLineTotal = (estimate, sectionId, value) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) => {
    if (s.id !== sectionId) return s;
    if (value === '' || value === null || value === undefined) {
      return { ...s, bottom_line_total: null };
    }
    const num = Number(value);
    return { ...s, bottom_line_total: Number.isFinite(num) ? num : null };
  }),
});

/** Cascade toggle: turn bottom-line mode ON or OFF for ALL sections at once. */
export const setAllSectionsBottomLineMode = (estimate, on) => ({
  ...estimate,
  sections: (estimate.sections || []).map((s) => ({
    ...s,
    bottom_line_mode: !!on,
    bottom_line_total: on ? s.bottom_line_total ?? null : null,
  })),
});

/* ---- Payment schedule ---------------------------------------------------- */
export const addPaymentRow = (estimate) => ({
  ...estimate,
  payment_schedule: [
    ...(estimate.payment_schedule || []),
    { id: genId('PS'), label: 'New milestone', percent: 0, due: '' },
  ],
});

export const updatePaymentRow = (estimate, rowId, patch) => ({
  ...estimate,
  payment_schedule: (estimate.payment_schedule || []).map((r) =>
    r.id === rowId ? { ...r, ...patch } : r
  ),
});

export const removePaymentRow = (estimate, rowId) => ({
  ...estimate,
  payment_schedule: (estimate.payment_schedule || []).filter((r) => r.id !== rowId),
});
